import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { Moment } from 'moment'
import React, { SyntheticEvent, useEffect, useMemo, useState } from 'react'
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable'
import { Resizable } from 'react-resizable'

import { useAppDispatch, useAppSelector } from '../../App/Hooks/reduxHooks'
import { eventPositionChange, setEventPosition } from '../store/calendarSlice'
import { IEvent, TActiveEventState } from '../types'

import {
  EventTile,
  EventInfo,
  EventText,
  IconContainer,
  IconSubContainer,
  InfoIcon,
  ResizeIcon,
} from './style'
import { IAppointmentPosition } from './types/IAppointmentPosition'
import { calculateYPosition } from './utils/calculateYPosition'
import { eventDragCoordinateUpdate } from './utils/eventDragCoordinateUpdate'
import { eventResizeCoordinateUpdate } from './utils/eventResizeCoordinateUpdate'

export interface IEventProps {
  event: IEvent
  columnWidth: number
  cellHeight: number
  pixelsPerHour: number
  currentColumn: number
  isActive: boolean
  onClicked: (event: IEvent) => void
  selectedStartDate: Moment
}

export const Event = ({
  event,
  columnWidth,
  cellHeight,
  pixelsPerHour,
  currentColumn,
  isActive,
  onClicked,
  selectedStartDate,
}: IEventProps) => {
  const [isDragDisabled, setIsDragDisabled] = useState<boolean>(false)
  const [tilePosition, setTilePosition] = useState<IAppointmentPosition>({
    x: columnWidth * currentColumn,
    y: cellHeight,
  })
  const [eventPixelHeight, setEventPixelHeight] = useState<number>(0)
  const [isDrag, setIsDrag] = useState<boolean>(false)
  const { endsAt, title, eventColor } = event

  const dispatch = useAppDispatch()
  const activeEventState = useAppSelector(eventPositionChange).state

  const onEventTileClick = () => {
    if (isDrag) {
      setIsDrag(false)
    }
    onClicked(event)
    return null
  }

  const onChangeEventPosition = (eventType: TActiveEventState): void => {
    if (activeEventState === 'idle') {
      dispatch(
        setEventPosition({
          eventPositionChange: { uuid: event.eventUuid, state: eventType },
        }),
      )
      if (eventType === 'drag') {
        setIsDrag(true)
      }
      if (eventType === 'resize') {
        setIsDragDisabled(true)
      }
    }
  }

  const duringResizeEvent = (
    resizeEvent: SyntheticEvent,
    size: { width: number; height: number },
  ): void => {
    setEventPixelHeight(size.height)
  }

  const eventResizeStop = (): void => {
    setIsDragDisabled(false)
    eventResizeCoordinateUpdate(
      event,
      tilePosition,
      columnWidth,
      eventPixelHeight,
      pixelsPerHour,
      selectedStartDate,
      dispatch,
    )
  }

  const eventDragStop = (e: DraggableEvent, eventDrag: DraggableData): void => {
    if (eventDrag.lastX !== eventDrag.x || eventDrag.lastY !== eventDrag.y) {
      eventDragCoordinateUpdate(
        event,
        eventDrag,
        columnWidth,
        eventPixelHeight,
        pixelsPerHour,
        selectedStartDate,
        dispatch,
      )
    }
  }

  // TODO GETS caleld on RESIZE and buggs the y position
  useEffect(() => {
    const midnightTime: Moment = event.startsAt.clone().startOf('day')
    setEventPixelHeight(
      calculateYPosition(event.startsAt, endsAt, pixelsPerHour),
    )
    setTilePosition({
      x: columnWidth * currentColumn,
      y: calculateYPosition(midnightTime, event.startsAt, pixelsPerHour),
    })
  }, [
    columnWidth,
    currentColumn,
    cellHeight,
    event.startsAt,
    endsAt,
    pixelsPerHour,
  ])

  return (
    <Draggable
      key={event.eventUuid}
      bounds="parent"
      grid={[columnWidth, cellHeight]}
      onStop={eventDragStop}
      position={tilePosition}
      disabled={!isActive || isDragDisabled}
      onDrag={() => onChangeEventPosition('drag')}>
      <Resizable
        height={eventPixelHeight}
        width={columnWidth}
        axis="y"
        draggableOpts={{ grid: [columnWidth, cellHeight] }}
        onResize={(resizeEvent, { size }) =>
          duringResizeEvent(resizeEvent, size)
        }
        onResizeStop={eventResizeStop}
        handle={
          <ResizeIcon
            isActive={isActive}
            icon="grip-lines"
            onMouseDownCapture={() => onChangeEventPosition('resize')}
          />
        }>
        <EventTile
          onClick={onEventTileClick}
          color={eventColor}
          blockHeight={eventPixelHeight}
          blockWidth={columnWidth}
          isActive={isActive}>
          <EventInfo>
            <EventText>
              {`${event.startsAt.format('hh:mm')} `}
              {title}
              <b>{` ${event.subTitle}`}</b>
              <br />
              <i>{event.text}</i>
            </EventText>
            <IconContainer>
              <IconSubContainer>
                {event.icons.left.map(icon => (
                  <InfoIcon
                    key={`${event.eventUuid}${icon}`}
                    icon={icon as IconProp}
                  />
                ))}
              </IconSubContainer>
              <IconSubContainer>
                {event.icons.right.map(icon => (
                  <InfoIcon
                    key={`${event.eventUuid}${icon}`}
                    icon={icon as IconProp}
                  />
                ))}
              </IconSubContainer>
            </IconContainer>
          </EventInfo>
        </EventTile>
      </Resizable>
    </Draggable>
  )
}

export interface IProps {
  event: IEvent
  columnWidth: number
  cellHeight: number
  pixelsPerHour: number
  currentColumn: number
  isActive: boolean
  onClicked: (event: IEvent) => void
  selectedStartDate: Moment
}

export const MemoizedEvent = ({
  event,
  columnWidth,
  cellHeight,
  pixelsPerHour,
  currentColumn,
  isActive,
  onClicked,
  selectedStartDate,
}: IProps) =>
  useMemo(
    () => (
      <Event
        key={event.id}
        event={event}
        columnWidth={columnWidth}
        cellHeight={cellHeight}
        pixelsPerHour={pixelsPerHour}
        currentColumn={currentColumn}
        isActive={isActive}
        onClicked={onClicked}
        selectedStartDate={selectedStartDate}
      />
    ),
    [isActive, cellHeight, event.startsAt],
  )
